import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Icon from "../../../components/Icon";
import SearchInput from "../../../components/SearchInput";
import ConfirmModal from "../../../components/ConfirmModal";
import Skeleton from "../../../components/Skeleton";
import { EmptyState } from "../../../components/State";
import {
  fetchIntegrations,
  fetchWebhookProviders,
  deleteIntegration,
  validateConnection,
} from "../services/IntegrationsService";
import IntegratedProviderCard from "../components/integrations/IntegratedProviderCard";
import IntegrationWizardModal from "../components/integrations/IntegrationWizardModal";
import WebhookPayloadTesterModal from "../components/integrations/WebhookPayloadTesterModal";

const DEFAULT_PROVIDERS = [
  {
    id: "jira",
    name: "Jira",
    category: "itsm",
    description: "Atlassian Jira issue lifecycle and webhook sync.",
    icon_url: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg",
    supported_events: ["issue_created", "issue_updated", "issue_deleted"],
    config_fields: [
      { name: "url", label: "Jira URL", required: true, type: "url", placeholder: "https://your-domain.atlassian.net", help_text: "Base URL of your Jira Cloud or Server instance" },
      { name: "email", label: "Email / Username", required: true, placeholder: "devops@company.com", help_text: "Jira user email or API account" },
      { name: "api_token", label: "API Token / Secret", required: true, type: "password", placeholder: "Enter Atlassian API token", help_text: "Atlassian API token or basic auth password" },
    ],
  },
  {
    id: "servicenow",
    name: "ServiceNow",
    category: "itsm",
    description: "ServiceNow Incident table events and state sync.",
    icon_url: "https://raw.githubusercontent.com/walkxcode/dashboard-icons/main/svg/servicenow.svg",
    supported_events: ["incident_created", "incident_updated", "change_request"],
    config_fields: [
      { name: "url", label: "Instance URL", required: true, type: "url", placeholder: "https://dev12345.service-now.com", help_text: "Base URL of your ServiceNow instance" },
      { name: "username", label: "Username", required: true, placeholder: "admin_api", help_text: "ServiceNow integration user account" },
      { name: "password", label: "Password / Secret", required: true, type: "password", placeholder: "••••••••••••", help_text: "ServiceNow integration user password" },
    ],
  },
];

export default function IntegrationsView() {
  const [integrations, setIntegrations] = useState([]);
  const [availableProviders, setAvailableProviders] = useState(DEFAULT_PROVIDERS);
  const [loading, setLoading] = useState(true);

  // Tab & Filters: 'configured' (default) vs 'available'
  const [activeTab, setActiveTab] = useState("configured");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Modals state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("jira");
  const [editingIntegration, setEditingIntegration] = useState(null);

  const [docModalOpen, setDocModalOpen] = useState(false);
  const [selectedDocIntegration, setSelectedDocIntegration] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toast feedback
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((msg, type = "success") => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast({ msg, type });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 4500);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const loadData = useCallback(async (showSkeleton = true) => {
    if (showSkeleton) setLoading(true);
    try {
      const [integrationsData, providersData] = await Promise.all([
        fetchIntegrations().catch((err) => {
          console.warn("Could not load integrations from backend, using empty list:", err);
          return [];
        }),
        fetchWebhookProviders().catch((err) => {
          console.warn("Could not load dynamic providers, using defaults:", err);
          return DEFAULT_PROVIDERS;
        }),
      ]);
      setIntegrations(Array.isArray(integrationsData) ? integrationsData : []);
      setAvailableProviders(
        Array.isArray(providersData) && providersData.length > 0
          ? providersData
          : DEFAULT_PROVIDERS
      );
    } catch (err) {
      console.warn("Could not load integrations:", err);
      setIntegrations([]);
      setAvailableProviders(DEFAULT_PROVIDERS);
    } finally {
      if (showSkeleton) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle open add wizard
  const handleOpenAdd = (providerKey) => {
    const defaultProv = providerKey || availableProviders[0]?.id || "jira";
    setSelectedProvider(defaultProv);
    setEditingIntegration(null);
    setWizardOpen(true);
  };

  // Handle edit
  const handleEdit = (integration) => {
    setEditingIntegration(integration);
    setSelectedProvider(integration.provider);
    setWizardOpen(true);
  };

  // Handle delete request
  const handleDeleteRequest = (integration) => {
    setIntegrationToDelete(integration);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!integrationToDelete) return;
    setDeleting(true);
    try {
      await deleteIntegration(integrationToDelete.id);
      showToast(`Integration "${integrationToDelete.name}" deleted`);
      setDeleteModalOpen(false);
      setIntegrationToDelete(null);
      loadData(false);
    } catch (err) {
      showToast(err.message || "Failed to delete integration", "error");
    } finally {
      setDeleting(false);
    }
  };

  // Test connection
  const handleTestConnection = async (integration) => {
    try {
      const res = await validateConnection({ integrationId: integration.id });
      if (res.success) {
        showToast(`Connection to ${integration.name} verified successfully!`);
      } else {
        showToast(`Connection validation failed: ${res.message}`, "error");
      }
      loadData(false);
    } catch (err) {
      showToast(err.message || "Connection validation failed", "error");
    }
  };

  // Open Webhook Docs
  const handleViewWebhookDocs = (provider, instance) => {
    setSelectedDocIntegration(
      instance
        ? {
            ...instance,
            provider: instance.provider || provider.id,
            isConfigured: true,
          }
        : {
            provider: provider.id,
            name: provider.name,
            isConfigured: false,
          }
    );
    setDocModalOpen(true);
  };

  // Group integrations by provider ID
  const instancesByProvider = useMemo(() => {
    const map = {};
    integrations.forEach((item) => {
      const p = item.provider?.toLowerCase();
      if (!p) return;
      if (!map[p]) map[p] = [];
      map[p].push(item);
    });
    return map;
  }, [integrations]);

  // Merge available providers from backend with any present in DB
  const allProviders = useMemo(() => {
    const list = [...availableProviders];
    const knownIds = new Set(list.map((p) => p.id.toLowerCase()));

    integrations.forEach((item) => {
      const pId = item.provider?.toLowerCase();
      if (pId && !knownIds.has(pId)) {
        list.push({
          id: pId,
          name: item.name || pId,
          category: item.category || "itsm",
          description: "Configured integration pipeline",
          supported_events: [],
          config_fields: [],
        });
        knownIds.add(pId);
      }
    });

    return list;
  }, [availableProviders, integrations]);

  // Configured view: ONLY providers with active configured instances in DB
  const configuredProviders = useMemo(() => {
    return allProviders.filter((prov) => {
      const instances = instancesByProvider[prov.id.toLowerCase()] || [];
      return instances.length > 0;
    });
  }, [allProviders, instancesByProvider]);

  // Available Directory: ALL supported providers (including Jira for multiple instances)
  const directoryProviders = useMemo(() => {
    return availableProviders;
  }, [availableProviders]);

  // Filter helper
  const matchesFilter = useCallback(
    (prov, instances = []) => {
      if (
        categoryFilter !== "all" &&
        prov.category?.toLowerCase() !== categoryFilter.toLowerCase()
      ) {
        return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesProvName = prov.name?.toLowerCase().includes(q);
        const matchesProvId = prov.id?.toLowerCase().includes(q);
        const matchesInstanceName = instances.some((inst) =>
          inst.name?.toLowerCase().includes(q)
        );
        if (!matchesProvName && !matchesProvId && !matchesInstanceName) {
          return false;
        }
      }
      return true;
    },
    [categoryFilter, searchQuery]
  );

  const filteredConfigured = useMemo(() => {
    return configuredProviders.filter((prov) =>
      matchesFilter(prov, instancesByProvider[prov.id.toLowerCase()] || [])
    );
  }, [configuredProviders, instancesByProvider, matchesFilter]);

  const filteredAvailable = useMemo(() => {
    return directoryProviders.filter((prov) =>
      matchesFilter(prov, instancesByProvider[prov.id.toLowerCase()] || [])
    );
  }, [directoryProviders, instancesByProvider, matchesFilter]);

  const hasConfiguredIntegrations = configuredProviders.length > 0;

  return (
    <div className="integrations-view-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`} role="status">
          <Icon
            name={toast.type === "success" ? "checkCircle" : "alert"}
            size={16}
          />
          <span className="toast-message">{toast.msg}</span>
          <button
            type="button"
            className="toast-close-btn"
            onClick={() => {
              if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
              setToast(null);
            }}
            aria-label="Close notification"
          >
            <Icon name="x" size={13} />
          </button>
        </div>
      )}

      {/* HEADER PANEL WITH CMDB-STYLE TABS */}
      <div
        style={{
          padding: "20px 24px 0",
          borderBottom: "1px solid #E2E8F0",
          background: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Integrations Management
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7280" }}>
              Configure external ITSM and observability webhook integrations, credentials, and real-time event sync.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => handleOpenAdd()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 6,
                background: "#1D4ED8",
                color: "#FFFFFF",
                border: "none",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(29, 78, 216, 0.15)",
                transition: "background 0.15s ease",
              }}
            >
              <Icon name="plus" size={14} color="#FFFFFF" />
              Add Integration
            </button>
          </div>
        </div>

        {/* CMDB-Style Tabs */}
        <div
          style={{
            display: "flex",
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab("configured")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 16px",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: activeTab === "configured" ? 700 : 500,
              color: activeTab === "configured" ? "#1D4ED8" : "#64748B",
              borderBottom:
                activeTab === "configured"
                  ? "2px solid #1D4ED8"
                  : "2px solid transparent",
              transition: "all 0.15s ease",
            }}
          >
            <Icon
              name="checkCircle"
              size={14}
              color={activeTab === "configured" ? "#1D4ED8" : "#94A3B8"}
            />
            Configured Integrations ({integrations.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("available")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 16px",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: activeTab === "available" ? 700 : 500,
              color: activeTab === "available" ? "#1D4ED8" : "#64748B",
              borderBottom:
                activeTab === "available"
                  ? "2px solid #1D4ED8"
                  : "2px solid transparent",
              transition: "all 0.15s ease",
            }}
          >
            <Icon
              name="grid"
              size={14}
              color={activeTab === "available" ? "#1D4ED8" : "#94A3B8"}
            />
            Available Directory ({availableProviders.length})
          </button>
        </div>
      </div>

      {/* FILTER & SEARCH BAR */}
      {!loading && (
        <div
          style={{
            padding: "12px 24px",
            background: "#FFFFFF",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 260, maxWidth: 420 }}>
            <SearchInput
              placeholder={
                activeTab === "configured"
                  ? "Search configured integrations..."
                  : "Search available integration catalog..."
              }
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="filter-select"
              style={{
                padding: "7px 30px 7px 12px",
                border: "1px solid #CBD5E1",
                borderRadius: 6,
                fontSize: 12.5,
                background: `#FFFFFF url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center`,
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                color: "#334155",
                cursor: "pointer",
                outline: "none",
                height: 36,
              }}
            >
              <option value="all">All Categories</option>
              <option value="itsm">ITSM & Ticketing</option>
              <option value="observability">Observability</option>
              <option value="notification">Notification</option>
            </select>
          </div>
        </div>
      )}

      {/* SCROLLABLE MAIN CONTENT AREA */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
        }}
      >
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={130} style={{ borderRadius: 10 }} />
            ))}
          </div>
        ) : activeTab === "configured" ? (
          /* CONFIGURED INTEGRATIONS TAB */
          !hasConfiguredIntegrations ? (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E5E7EB",
                padding: "32px 24px",
              }}
            >
              <EmptyState
                icon="link"
                title="No integrations configured yet"
                subtitle="Connect Jira, ServiceNow, or other supported tools to enable bi-directional incident and telemetry synchronization."
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 12,
                  marginTop: -16,
                  marginBottom: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() => handleOpenAdd()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 18px",
                    borderRadius: 7,
                    background: "#1D4ED8",
                    color: "#FFFFFF",
                    border: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(29, 78, 216, 0.15)",
                  }}
                >
                  <Icon name="plus" size={13} color="#FFFFFF" />
                  Add Integration
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("available")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 18px",
                    borderRadius: 7,
                    background: "#F8FAFC",
                    color: "#334155",
                    border: "1px solid #CBD5E1",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Browse Available Directory
                </button>
              </div>
            </div>
          ) : filteredConfigured.length === 0 ? (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E5E7EB",
                padding: "24px 20px",
              }}
            >
              <EmptyState
                icon="search"
                title="No configured integrations match your search"
                subtitle="Try clearing the search query or adjusting your category filter."
              />
              <div style={{ textAlign: "center", marginTop: -20, marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setCategoryFilter("all");
                  }}
                  style={{
                    padding: "7px 16px",
                    background: "#fff",
                    color: "#374151",
                    border: "1px solid #CBD5E1",
                    borderRadius: 7,
                    cursor: "pointer",
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  Reset Filters
                </button>
              </div>
            </div>
          ) : (
            <div className="unified-integrations-grid">
              {filteredConfigured.map((provider) => (
                <IntegratedProviderCard
                  key={provider.id}
                  provider={provider}
                  instances={instancesByProvider[provider.id.toLowerCase()] || []}
                  onConnect={handleOpenAdd}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                  onTestConnection={handleTestConnection}
                  onViewWebhookDocs={handleViewWebhookDocs}
                />
              ))}
            </div>
          )
        ) : (
          /* AVAILABLE CATALOG / DIRECTORY TAB */
          <div>
            <div className="catalog-intro-banner">
              <div className="banner-title-flex">
                <h3>Available Integration Connectors</h3>
                <span className="live-counter-badge">
                  {availableProviders.length} Connectors Available
                </span>
              </div>
              <p>
                Connect Jira, ServiceNow, or custom webhooks to ingest incoming incident payloads, synchronize status updates, and trigger automated Root Cause Analysis.
              </p>
            </div>

            {filteredAvailable.length === 0 ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #E5E7EB",
                  padding: "24px 20px",
                }}
              >
                <EmptyState
                  icon="search"
                  title="No connectors found"
                  subtitle="No integrations in the directory match your search or filter."
                />
              </div>
            ) : (
              <div className="unified-integrations-grid">
                {filteredAvailable.map((provider) => (
                  <IntegratedProviderCard
                    key={provider.id}
                    provider={provider}
                    instances={instancesByProvider[provider.id.toLowerCase()] || []}
                    isCatalog={true}
                    onConnect={handleOpenAdd}
                    onEdit={handleEdit}
                    onDelete={handleDeleteRequest}
                    onTestConnection={handleTestConnection}
                    onViewWebhookDocs={handleViewWebhookDocs}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wizard Modal (Add / Edit) */}
      <IntegrationWizardModal
        isOpen={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setEditingIntegration(null);
        }}
        initialProvider={selectedProvider}
        availableProviders={availableProviders}
        existingIntegration={editingIntegration}
        onSuccess={(saved) => {
          loadData(false);
          setActiveTab("configured");
          const displayName = saved?.name || "Integration";
          showToast(
            editingIntegration
              ? `Integration "${displayName}" updated successfully`
              : `Integration "${displayName}" configured successfully!`
          );
        }}
      />

      {/* Webhook Payload Tester & Setup Modal */}
      <WebhookPayloadTesterModal
        isOpen={docModalOpen}
        onClose={() => {
          setDocModalOpen(false);
          setSelectedDocIntegration(null);
        }}
        integration={selectedDocIntegration}
        onConnect={(provId) => {
          setDocModalOpen(false);
          setSelectedDocIntegration(null);
          handleOpenAdd(provId);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title="Delete Integration Pipeline"
        message={`Are you sure you want to delete "${integrationToDelete?.name}"? Any incoming webhooks sent to its endpoint will be immediately blocked and rejected.`}
        confirmText={deleting ? "Deleting..." : "Delete Integration"}
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setIntegrationToDelete(null);
        }}
      />
    </div>
  );
}
